import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { onSnapshotWithRetry as onSnapshot } from '../utils/firestoreSubscribe';
import { getDb } from '../firebase';
import { subDays, differenceInDays, parseISO } from 'date-fns';

// Maximum length of a single trip block, in days. Bounds the date-range
// expansion in applyOverrides and keeps a fat-fingered range from painting years.
export const MAX_TRIP_DAYS = 60;

export const createCustodySchedule = async (familyId, scheduleData) => {
  const db = getDb();
  const existingQuery = query(
    collection(db, 'families', familyId, 'custodySchedules'),
    where('isActive', '==', true)
  );
  const existingSnap = await getDocs(existingQuery);
  const newStart = scheduleData.startDate instanceof Date
    ? scheduleData.startDate
    : new Date(scheduleData.startDate);
  const dayBefore = subDays(newStart, 1);

  const endDatePromises = existingSnap.docs.map(d =>
    updateDoc(d.ref, { endDate: dayBefore })
  );
  await Promise.all(endDatePromises);

  const ref = doc(collection(db, 'families', familyId, 'custodySchedules'));
  const data = {
    ...scheduleData,
    createdAt: Timestamp.now(),
    isActive: true
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

export const updateCustodySchedule = async (familyId, scheduleId, updates) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'custodySchedules', scheduleId), updates);
};

export const deactivateCustodySchedule = async (familyId, scheduleId) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'custodySchedules', scheduleId), {
    isActive: false
  });
};

export const subscribeToCustodySchedules = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'custodySchedules'),
    where('isActive', '==', true)
  );
  return onSnapshot(q, (snapshot) => {
    const schedules = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    callback(schedules);
  }, (error) => {
    console.error('Custody schedules listener error:', error);
    callback([]);
  });
};

export const createCustodyOverride = async (familyId, overrideData) => {
  const db = getDb();
  const ref = doc(collection(db, 'families', familyId, 'custodyOverrides'));
  const data = {
    ...overrideData,
    createdAt: Timestamp.now()
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

// Create a trip / vacation block: a single custodyOverride document that spans a
// date range and assigns the whole range to one parent once confirmed. Reuses the
// custodyOverrides collection so it flows through the same approval + notification
// pipeline as a single-day swap. Centralizes the trip invariants:
//   type = 'trip', date = startDate (keeps it in the orderBy('date') stream),
//   newParentId = the chosen parent.
export const createTrip = async (familyId, {
  startDate,
  endDate,
  assignedParentId,
  originalParentId,
  title,
  reason,
  proposedBy
}) => {
  if (!startDate || !endDate) throw new Error('Trip requires a start and end date');
  if (endDate < startDate) throw new Error('End date must be on or after the start date');
  if (!assignedParentId) throw new Error('Trip requires a parent to assign the days to');
  if (differenceInDays(parseISO(endDate), parseISO(startDate)) > MAX_TRIP_DAYS) {
    throw new Error(`A trip can be at most ${MAX_TRIP_DAYS} days`);
  }

  return createCustodyOverride(familyId, {
    type: 'trip',
    date: startDate, // anchor day for orderBy('date') + history rendering
    startDate,
    endDate,
    title: title?.trim() || null,
    originalParentId: originalParentId || proposedBy,
    newParentId: assignedParentId,
    reason: reason?.trim() || 'vacation',
    note: reason?.trim() || null,
    status: 'proposed',
    proposedBy,
    respondedBy: null
  });
};

export const respondToOverride = async (familyId, overrideId, status, respondedBy) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'custodyOverrides', overrideId), {
    status,
    respondedBy
  });
};

export const subscribeToCustodyOverrides = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'custodyOverrides'),
    orderBy('date', 'asc')
  );
  return onSnapshot(q, (snapshot) => {
    const overrides = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(overrides);
  }, (error) => {
    console.error('Custody overrides listener error:', error);
  });
};

export const deleteCustodyOverride = async (familyId, overrideId) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'custodyOverrides', overrideId));
};
